import * as storage from '../utils/storage';
import Button from '../components/Button/Button';
import Loading from '../components/Loading/Loading';
import { H2 } from '../config/designSystem/typography';
import { SafeAreaView } from 'react-native-safe-area-context';
import { View } from 'react-native';
import { concatPath } from '../utils/links/concatPath';
import { dashboardKey, dashboardStackKey, privateKey, publicKey } from '../constants/routeKeys';
import { loginKey, tabsKey } from '../constants/routeKeys';
import { observer } from 'mobx-react';
import { redirectStringForStore } from '../constants/storageKeys';
import { router, useGlobalSearchParams, usePathname, useSegments } from 'expo-router';
import { useEffect, useRef } from 'react';
import { useStores } from '../hooks/useStores';

const NotFound = observer(() => {
  const rootStore = useStores();
  const user = rootStore?.user;

  // Report WHICH route failed to resolve. Landing here is always a navigation bug, but
  // the screen itself says nothing about the target, which makes it near-impossible to
  // diagnose from a user report ("I get Oops at launch"). One line in the logs names it.
  const pathname = usePathname();
  const segments = useSegments();
  const params = useGlobalSearchParams();
  // usePathname() is GLOBAL: it reports whatever route is current, not the one this
  // screen was mounted for. This screen also stays mounted underneath after redirecting
  // away, so reading it live reported "/dashboard" -- the destination, not the failure.
  // Capture the path that actually failed on first render and use that throughout.
  const failedPathRef = useRef(pathname);
  const failedPath = failedPathRef.current;

  // Landing here means the last navigation target did not resolve. If that target came
  // from the stored post-login redirect, leaving it in storage replays it on the next
  // launch and pins the user on this screen forever -- it is only cleared inside the
  // (private) group, which is exactly where a bad redirect never arrives. Drop it so
  // an already-poisoned install recovers by itself.
  //
  // Same "/" carve-out as the log below, and for a sharper reason: "/" routes through
  // here on EVERY cold launch by design, so clearing unconditionally wiped the pending
  // deep link every single launch -- which is the exact feature the redirect exists to
  // provide. Only a genuinely unresolved path is evidence of a poisoned redirect.
  useEffect(() => {
    if (failedPath === '/') return;
    storage.remove(redirectStringForStore);
  }, [failedPath]);

  useEffect(() => {
    // "/" is expected here and handled by RedirectApp, so it is not worth an error toast
    // (or a Sentry exception -- console.error is captured) on every launch. Any OTHER
    // unmatched path is a real navigation bug and still shouts; that is how "/" and the
    // bogus /(private)/(public)/(Tabs)/login were both found.
    if (failedPath === '/') return;
    console.error(
      '[not-found] no route matched: ' + JSON.stringify({ pathname: failedPath, segments, params })
    );
  }, [failedPath]);

  const hasUser = !!user?.user;
  const isAnon = user?.anonymous;



  const authenticatedUser = hasUser && !isAnon;
  // Absolute (-1). These were relative (order 0), which is the one thing that cannot
  // work from this screen: the whole reason the user is here is that the current route
  // did not resolve, so there is no sane base to be relative TO. The login target was
  // also missing its (Tabs) group, so it pointed at a route that does not exist.
  const goToMain = () => {
    if(authenticatedUser) {
      router.replace(concatPath([privateKey, dashboardStackKey, dashboardKey], -1) as string);
    } else {
      router.replace(concatPath([publicKey, tabsKey, loginKey], -1) as string);
    }
  };



  // "/" lands here for one beat on every launch -- it is not a route, and RedirectApp
  // replaces it as soon as its effect runs. Show a loader for that case rather than
  // flashing "this page does not exist" at the user for something that is neither their
  // fault nor a real dead end any more. Purely cosmetic: the redirect is RedirectApp's.
  if (failedPath === '/') {
    return (
      <View style={{flex: 1, backgroundColor: '#fff'}}>
        <Loading withLabel={false} />
      </View>
    );
  }

  return(
    <SafeAreaView style={{flex:1, alignItems: 'center', justifyContent: 'center', columnGap: 40}}>
      <H2>Oops, this page does not exist or has been deleted.</H2>
      <Button onPress={goToMain} label={'Go to main'} />
    </SafeAreaView>
  );
});

export default NotFound;